import { useState } from 'react';
import { useTenant } from '@/app/providers/TenantProvider';
import { useAuth } from '@/app/providers/AuthProvider';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { ShieldCheck, Download, Trash2, HelpCircle } from 'lucide-react';

export function ProfilePrivacy() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  
  // Consentimentos locais (LGPD Art. 7º, I)
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Manipulador de salvamento de novos consentimentos (Grava no user_consents e registra auditoria)
  const handleConsentChange = async (type: string, currentVal: boolean) => {
    if (!user) return;
    
    const newVal = !currentVal;
    try {
      // 1. Grava histórico na tabela user_consents (Guideline 07-lgpd.md e 04-database.md)
      const { error: consentError } = await supabase
        .from('user_consents')
        .insert({
          user_id: user.id,
          consent_type: type,
          consent_version: 'v1.0.0',
          granted: newVal,
          ip_address: null, // Deixamos nulo em dev para resolução em runtime pelo Postgres
          user_agent: navigator.userAgent,
        });

      if (consentError) throw consentError;

      // 2. Registra na trilha imutável de audit_logs (Guideline 11-multi-tenant.md e 05-audit-logs)
      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert({
          tenant_id: tenantId,
          user_id: user.id,
          action: newVal ? 'consent_grant' : 'consent_revoke',
          resource: 'user_consents',
          legal_basis: 'art_7_i_consent',
          metadata: { consent_type: type, version: 'v1.0.0' },
        });

      if (auditError) throw auditError;

      if (type === 'analytics') setAnalyticsConsent(newVal);
      if (type === 'marketing') setMarketingConsent(newVal);
      
      toast.success('Seus consentimentos foram atualizados e registrados com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao atualizar consentimento.');
    }
  };

  // Solicitar Exportação de Dados (LGPD Art. 18, V)
  const handleExportData = async () => {
    if (!user) return;
    setExportLoading(true);
    
    try {
      // 1. Adiciona registro de exportação no audit_logs
      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert({
          tenant_id: tenantId,
          user_id: user.id,
          action: 'export_data',
          resource: 'profiles',
          resource_id: user.id,
          legal_basis: 'art_7_v_contract',
          metadata: { format: 'json/zip', requested_at: new Date().toISOString() },
        });

      if (auditError) throw auditError;

      // Em produção, isso dispararia uma Edge Function ou Job assíncrono para gerar um arquivo ZIP.
      // Em dev, geramos um JSON rápido contendo os dados mockados do perfil dele para simular o download de forma impecável.
      const exportJson = {
        plataforma: 'Lumenise Base',
        titular: user.email,
        solicitacao: new Date().toISOString(),
        perfil: {
          id: user.id,
          email: user.email,
          criado_em: user.created_at,
        },
        consentimentos: [
          { tipo: 'terms', concedido: true },
          { tipo: 'analytics', concedido: analyticsConsent },
          { tipo: 'marketing', concedido: marketingConsent },
        ]
      };

      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportJson, null, 2));
      const downloadAnchor = document.createElement('a');
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", `lumenise-dados-titular-${user.id}.json`);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();

      toast.success('Relatório de dados pessoais exportado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar exportação de dados.');
    } finally {
      setExportLoading(false);
    }
  };

  // Solicitar Exclusão de Conta (LGPD Art. 18, VI)
  const handleDeleteRequest = async () => {
    if (!user) return;
    
    const confirmDelete = window.confirm(
      'ATENÇÃO: A exclusão da conta é permanente e removerá todos os seus dados pessoais associados. O prazo de atendimento sob SLA é de 15 dias. Deseja prosseguir?'
    );
    
    if (!confirmDelete) return;
    setDeleteLoading(true);

    try {
      // Registrar no audit_log imutável o pedido de exclusão
      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert({
          tenant_id: tenantId,
          user_id: user.id,
          action: 'delete_account',
          resource: 'profiles',
          resource_id: user.id,
          legal_basis: 'art_7_ii_legal_obligation',
          metadata: { ip: null, sla_days: 15 },
        });

      if (auditError) throw auditError;

      toast.success('Sua solicitação de exclusão foi enviada. SLA de resposta: 15 dias úteis.');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao processar solicitação.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Informação Geral LGPD */}
      <div className="flex items-start space-x-4 p-4 rounded-xl border border-accent/20 bg-accent/5">
        <ShieldCheck className="h-6 w-6 text-accent shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h4 className="text-sm font-bold text-ink">Seus Direitos Garantidos (LGPD)</h4>
          <p className="text-xs text-ink-muted leading-relaxed">
            Em conformidade com a Lei Federal nº 13.709/2018 (Lei Geral de Proteção de Dados), disponibilizamos mecanismos simples e rastreáveis para você gerenciar seus consentimentos, solicitar portabilidade de dados e requerer a exclusão permanente de seus dados pessoais em nossa base.
          </p>
        </div>
      </div>

      {/* Consentimentos Granulares */}
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-bold text-ink font-display">Consentimentos Ativos</h3>
          <p className="text-ink-muted text-xs">Ative ou revogue permissões de coleta de dados a qualquer momento.</p>
        </div>

        <div className="space-y-3">
          {/* Termos de Uso (Obrigatório / Base Contratual) */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-hairline bg-midnight-2/20">
            <div>
              <div className="flex items-center space-x-1.5">
                <p className="text-sm font-semibold text-ink">Termos de Uso e Política de Privacidade</p>
                <span title="Base legal: Execução de Contrato. Obrigatório para uso da plataforma.">
                  <HelpCircle className="h-3.5 w-3.5 text-ink-muted cursor-help" />
                </span>
              </div>
              <p className="text-xs text-ink-muted">Autoriza o tratamento de dados pessoais estritamente necessários para viabilizar sua conta.</p>
            </div>
            <div className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-200">
              Obrigatório
            </div>
          </div>

          {/* Consentimento Analítico */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-hairline bg-midnight-2/40">
            <div>
              <p className="text-sm font-semibold text-ink">Análise de Desempenho e Telemetria</p>
              <p className="text-xs text-ink-muted">Utiliza dados comportamentais (cliques, caminhos de navegação) para melhorar os fluxos e usabilidade do painel.</p>
            </div>
            <button
              onClick={() => handleConsentChange('analytics', analyticsConsent)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${analyticsConsent ? 'bg-accent' : 'bg-border'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-midnight-4 shadow ring-0 transition duration-200 ease-in-out ${analyticsConsent ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Consentimento de Marketing */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-hairline bg-midnight-2/40">
            <div>
              <p className="text-sm font-semibold text-ink">Comunicações e Boletins</p>
              <p className="text-xs text-ink-muted">Permite o envio de informativos sobre a plataforma e dicas estratégicas de gestão.</p>
            </div>
            <button
              onClick={() => handleConsentChange('marketing', marketingConsent)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${marketingConsent ? 'bg-accent' : 'bg-border'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-midnight-4 shadow ring-0 transition duration-200 ease-in-out ${marketingConsent ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Direitos do Titular (Art. 18) */}
      <div className="space-y-4 border-t border-hairline/80 pt-6">
        <div>
          <h3 className="text-base font-bold text-ink font-display">Ações de Privacidade e Portabilidade</h3>
          <p className="text-ink-muted text-xs">Exerça seus direitos de titular de dados garantidos pela lei.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card de Exportação */}
          <div className="p-5 rounded-xl border border-hairline bg-midnight-2/20 flex flex-col justify-between space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-bold text-ink flex items-center space-x-1.5">
                <Download className="h-4 w-4 text-accent" />
                <span>Portabilidade (Art. 18, V)</span>
              </p>
              <p className="text-xs text-ink-muted leading-relaxed">
                Baixe um arquivo estruturado contendo todos os dados pessoais associados à sua conta armazenados em nossos servidores.
              </p>
            </div>
            <button
              onClick={handleExportData}
              disabled={exportLoading}
              className="w-full flex items-center justify-center space-x-2 rounded-xl bg-midnight-4 hover:bg-midnight-5 text-ink border border-hairline font-semibold py-2.5 px-4 text-xs transition-colors cursor-pointer"
            >
              <span>{exportLoading ? 'Gerando arquivo...' : 'Exportar meus dados'}</span>
            </button>
          </div>

          {/* Card de Exclusão */}
          <div className="p-5 rounded-xl border border-red-600/10 bg-red-600/[0.02] flex flex-col justify-between space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-bold text-red-600 flex items-center space-x-1.5">
                <Trash2 className="h-4 w-4" />
                <span>Excluir conta (Art. 18, VI)</span>
              </p>
              <p className="text-xs text-ink-muted leading-relaxed">
                Solicite o encerramento permanente e a exclusão irreversível de sua conta e dados sob o protocolo LGPD (SLA de 15 dias).
              </p>
            </div>
            <button
              onClick={handleDeleteRequest}
              disabled={deleteLoading}
              className="w-full flex items-center justify-center space-x-2 rounded-xl bg-red-600/10 border border-red-600/20 text-red-400 hover:bg-red-600/20 font-semibold py-2.5 px-4 text-xs transition-colors cursor-pointer"
            >
              <span>{deleteLoading ? 'Processando solicitação...' : 'Solicitar Exclusão'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
